import React, { useState, useEffect, useCallback, useRef, useImperativeHandle, forwardRef } from 'react';
import { Card, Badge, Accordion, Alert } from 'react-bootstrap';
import { patchMedicionIBC, patchPatronesBulk, patchPuntosCalibracion } from '../../../../api/common';
import AutoguardadoIndicator from '../components/AutoguardadoIndicator';
import PuntosMedicionTable from '../components/Puntosmediciontable';
import PuntosCalibracionForm from '../components/PuntosCalibracionForm';
import { 
  convertirPatronesAEsquema,  
  convertirIBCAEsquema,
  convertirEsquemaAPatrones,
  convertirEsquemaAIBC
} from '../../../../helpers/conversionEsquemas';

const StepIBC = forwardRef(({ loteEquipos, lotesCalibracion, plantilla, onDataChange }, ref) => {

  const getMagnitudKey = (m) => m?.magnitud ?? m?.nombre;
  const equiposEditadosRef = useRef(new Set()); // Para trackear qué equipos han sido editados y necesitan guardado
  const patronesEditadosRef = useRef(new Set()); // Para trackear si se han editado patrones y necesitan guardado
  // ═══════════════════════════════════════════════════════════════
  // ESTADOS PARA PUNTOS DE CALIBRACION GENERAL
  // ═══════════════════════════════════════════════════════════════
    const [datosPuntosCalibracion, setDatosPuntosCalibracion] = useState({});

    console.log('Datos puntos calibración en estado:', datosPuntosCalibracion);
    const [datoHisteresis, setDatoHisteresis] = useState({});

    // ═══════════════════════════════════════════════════════════════
  // ESTADOS PARA PATRONES
  // ═══════════════════════════════════════════════════════════════
  const [datosPatrones, setDatosPatrones] = useState({});
  console.log('Datos patrones en estado:', datosPatrones);

  // ═══════════════════════════════════════════════════════════════
  // ESTADOS PARA IBC
  // ═══════════════════════════════════════════════════════════════
  const [datosIBC, setDatosIBC] = useState({});
  console.log('Datos IBC en estado:', datosIBC);
  // ═══════════════════════════════════════════════════════════════
  // AUTOGUARDADO UNIFICADO
  // ═══════════════════════════════════════════════════════════════
  const [autoguardado, setAutoguardado] = useState({
    estado: 'idle',
    tipo: null,
    mensaje: ''
  });
  const timerPatronesRef = useRef(null);
  const timersEquiposRef = useRef({});
  const timerPuntosCalibracionRef = useRef(null);

  // Fuente de verdad: cantidad de patrones PRINCIPALES y array de principales
  // reales. Prioriza override per-lote (Step 1 personalización) y descarta los
  // medios de calibración (opcionales — no cuentan como patrones requeridos).
  // Antes se leía solo de `plantilla.cantPatronesRequeridos` → aparecía 1 fila
  // en Mediciones de Patrones aunque la personalización fuera a 2.
  const cantPatronesPrincipales = React.useMemo(() => {
    const primerLote = lotesCalibracion?.[0];
    return (primerLote?.cantPatronesRequeridos ?? plantilla?.cantPatronesRequeridos) || 0;
  }, [lotesCalibracion, plantilla]);

  const patronesPrincipalesDelLote = React.useMemo(() => {
    const primerLote = lotesCalibracion?.[0];
    const idsMedios = new Set();
    (plantilla?.magnitudesConfig || []).forEach((m) => {
      const c = m.medioCalibracion;
      if (c) idsMedios.add(String(c?._id ?? c));
    });
    return (primerLote?.patrones || []).filter((p) => !idsMedios.has(String(p?._id ?? p)));
  }, [lotesCalibracion, plantilla]);

  // ═══════════════════════════════════════════════════════════════
  // INICIALIZAR DATOS DE PATRONES
  // ═══════════════════════════════════════════════════════════════
  useEffect(() => {
    if (!plantilla) return;

    const datos = {};
    const cantPatrones = cantPatronesPrincipales;

    Array.from({ length: cantPatrones }).forEach((_, patronIdx) => {
      datos[patronIdx] = {};

      const patronReal = patronesPrincipalesDelLote[patronIdx] || null;

      (plantilla.magnitudesConfig || []).forEach((m, idxMag) => {
        const magnitudKey = getMagnitudKey(m);
        // Crear estructura base con información del patrón
        const estructura = {
          patron: patronReal || null,
        };

        // Si existen datos guardados, convertirlos del esquema DB al formato UI
        if (lotesCalibracion && lotesCalibracion.length > 0) {
          const primerLote = lotesCalibracion[0];
          const sesion = primerLote.sesiones?.[idxMag];
          const medicionesPatrones = sesion?.medicionesPatrones || [];

          // Convertir usando la función auxiliar
          const datosConvertidos = convertirEsquemaAPatrones(medicionesPatrones, m, patronIdx);
          Object.assign(estructura, datosConvertidos);
        } else {
          // Si no hay datos, crear estructura vacía
          estructura.puntoCalibracion = Array(m.cantPuntos).fill('');
          estructura.mediciones = Array(m.cantPuntos).fill(null).map(() => Array(m.cantMedicionesPorPunto).fill(''));
          
          if (m.requiereHisteresis && m.histeresisEnTodosLosPuntos) {
            estructura.puntoBajadaCalibracion = Array(m.cantPuntos).fill('');
            estructura.medicionesSubida = Array(m.cantPuntos).fill(null).map(() => Array(m.cantMedicionesPorPunto).fill(''));
            estructura.medicionesBajada = Array(m.cantPuntos).fill(null).map(() => Array(m.cantMedicionesPorPunto).fill(''));
          } else if (m.requiereHisteresis && !m.histeresisEnTodosLosPuntos) {
            estructura.puntoHisteresis = {
              puntoCal: '',
              mediciones: Array(m.cantMedicionesPorPunto).fill('')
            };
          }
        }

        datos[patronIdx][magnitudKey] = estructura;
      });
    });

    setDatosPatrones(datos);
  }, [lotesCalibracion, plantilla]);

  // ═══════════════════════════════════════════════════════════════
  // INICIALIZAR PUNTOS DE CALIBRACIÓN
  // ═══════════════════════════════════════════════════════════════
  useEffect(() => {
    // Validación defensiva
    if (!lotesCalibracion || lotesCalibracion.length === 0 || !lotesCalibracion[0] || !lotesCalibracion[0].sesiones) {
      return;
    }

    const puntosCal = {};
    const histeresis = {};

    (lotesCalibracion[0].sesiones || []).forEach((m, idxMag) => {
      puntosCal[m.magnitud] = m.puntosCalibracion || [];
      // Hidratar `datoHisteresis` desde las mediciones guardadas: la histéresis se
      // persiste por equipo dentro de `medicionesIBC[].puntoCal` con
      // `histeresis:true`. Todos los equipos comparten el mismo valor, así que
      // basta con leer el primero disponible. Sin esto el input quedaba en
      // blanco tras refrescar aunque el dato existiera.
      if (m.histeresis && !m.histeresisEnTodosLosPuntos) {
        const cantPuntos = m.cantidadPuntos ?? m.cantPuntos ?? 0;
        let valorHist = '';
        for (const lote of lotesCalibracion) {
          const medIBC = lote?.sesiones?.[idxMag]?.medicionesIBC || [];
          // Fallback dual: flag `histeresis:true` (nuevo) o posición extra
          // más allá de `cantPuntos` (esquema legacy).
          const puntoHist = medIBC.find((p) => p?.histeresis === true)
            || (medIBC.length > cantPuntos ? medIBC[cantPuntos] : null);
          if (puntoHist?.puntoCal !== undefined && puntoHist?.puntoCal !== null && puntoHist?.puntoCal !== '') {
            valorHist = puntoHist.puntoCal;
            break;
          }
        }
        histeresis[m.magnitud] = valorHist;
      }
    });

    setDatosPuntosCalibracion(puntosCal);
    setDatoHisteresis(histeresis);
  }, [lotesCalibracion, plantilla]);

  // ═══════════════════════════════════════════════════════════════
  // INICIALIZAR DATOS DE IBC
  // ═══════════════════════════════════════════════════════════════
  useEffect(() => {
    if (!lotesCalibracion || lotesCalibracion.length === 0 || !lotesCalibracion[0] || !lotesCalibracion[0].sesiones) {
      return;
    }

    const datos = {};
    const estados = {};
    lotesCalibracion.forEach((lote, loteIdx) => {
      const equipoId = lote.equipoInOt?._id ?? lote.equipoInOt;
      datos[equipoId] = {};
      estados[equipoId] = 'idle';

      (lotesCalibracion[0].sesiones || []).forEach((m, idxMag) => {
        const magnitudKey = getMagnitudKey(m);
        const sesion = lote.sesiones?.[idxMag];
        const medicionesIBC = sesion?.medicionesIBC;

        if (medicionesIBC && medicionesIBC.length > 0) {
          datos[equipoId][magnitudKey] = convertirEsquemaAIBC(medicionesIBC, m);
        } else {
          // Siempre inicializar todas las propiedades para evitar undefined
          const estructura = {
            puntoCalibracion: Array(m.cantidadPuntos).fill(''),
            mediciones: Array(m.cantidadPuntos).fill(null).map(() => Array(m.cantMedicionesPorPunto).fill('')),
            puntoBajadaCalibracion: null,
            medicionesSubida: null,
            medicionesBajada: null,
            puntoHisteresis: null,
          };

          if (m.histeresis && m.histeresisEnTodosLosPuntos) {
            estructura.puntoBajadaCalibracion = Array(m.cantidadPuntos).fill('');
            estructura.medicionesSubida = Array(m.cantidadPuntos).fill(null).map(() => Array(m.cantMedicionesPorPunto).fill(''));
            estructura.medicionesBajada = Array(m.cantidadPuntos).fill(null).map(() => Array(m.cantMedicionesPorPunto).fill(''));
          } else if (m.histeresis && !m.histeresisEnTodosLosPuntos) {
            estructura.puntoHisteresis = {
              puntoCal: '',
              mediciones: Array(m.cantMedicionesPorPunto).fill('')
            };
          }

          datos[equipoId][magnitudKey] = estructura;
        }
      });
    });
    setDatosIBC(datos);
  }, [lotesCalibracion]);

  // ═══════════════════════════════════════════════════════════════
  // GUARDAR CAMBIOS DE PATRONES
  // ═══════════════════════════════════════════════════════════════
  const guardarCambiosPatrones = useCallback(async (todosLosDatos /*, patronesEditados (ignorado — ver comentario)*/) => {
    setAutoguardado({ estado: 'saving', tipo: 'patrones', mensaje: 'Guardando patrones...' });
    try {
      if (!lotesCalibracion || lotesCalibracion.length === 0 || !lotesCalibracion[0] || !lotesCalibracion[0].sesiones) {
        console.error('lotesCalibracion no está disponible');
        setAutoguardado({ estado: 'error', tipo: 'patrones', mensaje: 'Error: datos no disponibles' });
        return;
      }

      // Bug fix — DOS PROBLEMAS CRÍTICOS anteriores:
      // 1) `patronesReales` se construía iterando `lotesCalibracion` (equipos)
      //    y leyendo `lote.patron` — que NO existe en el schema (los patrones
      //    viven en `lote.patrones[]`, array por lote). Resultado: patronId
      //    siempre `undefined` en cada medicionPatron guardada.
      // 2) El save enviaba SOLO los patrones editados y el backend REEMPLAZA
      //    el array `sesiones[i].medicionesPatrones` con lo recibido → cuando
      //    el usuario editaba solo el patrón 2, se sobrescribía el patrón 1.
      //
      // Fix: (a) leer patrones reales desde `lotesCalibracion[0].patrones`.
      //      (b) enviar SIEMPRE TODOS los patrones (todosLosDatos completo)
      //          para que el reemplazo del backend no pierda información.
      // Usar solo PATRONES PRINCIPALES (filtra medios de calibración via memo).
      // El índice debe coincidir con el de `patronesVirtuales` para que
      // `convertirPatronesAEsquema` resuelva correctamente `patronId` por idx.
      const patronesReales = {};
      patronesPrincipalesDelLote.forEach((p, idx) => {
        patronesReales[idx] = (p && typeof p === 'object') ? p : { _id: p };
      });

      // Iterar por cada magnitud y enviar sus mediciones.
      const promesas = lotesCalibracion[0].sesiones.map((magnitudConfig, sesionIndex) => {
        const magnitudNombre = getMagnitudKey(magnitudConfig);
        const puntosCalibracion = datosPuntosCalibracion[magnitudNombre] || [];

        const medicionesPatrones = convertirPatronesAEsquema(
          todosLosDatos, // TODOS los patrones — no solo los editados
          magnitudNombre,
          magnitudConfig,
          puntosCalibracion,
          patronesReales
        );

        console.log(`Guardando patrones magnitud=${magnitudNombre} (sesion ${sesionIndex}) — entries: ${medicionesPatrones.length}`);
        return patchPatronesBulk(loteEquipos._id, sesionIndex, { medicionesPatrones });
      });

      await Promise.all(promesas);
      setAutoguardado({ estado: 'saved', tipo: 'patrones', mensaje: 'Patrones guardados correctamente' });
      if (onDataChange) onDataChange();
      setTimeout(() => setAutoguardado({ estado: 'idle', tipo: null, mensaje: '' }), 3000);
    } catch (error) {
      console.error('Error guardando patrones:', error);
      setAutoguardado({ estado: 'error', tipo: 'patrones', mensaje: 'Error al guardar patrones' });
    }
  }, [loteEquipos, lotesCalibracion, datosPuntosCalibracion, onDataChange, patronesPrincipalesDelLote]);

  // ═══════════════════════════════════════════════════════════════
  // GUARDAR CAMBIOS DE IBC (TODOS LOS EQUIPOS)
  // ═══════════════════════════════════════════════════════════════
  const guardarCambiosEquipos = useCallback(async (todosLosDatos, equiposEditados) => {
    setAutoguardado({ estado: 'saving', tipo: 'equipos', mensaje: 'Guardando equipos...' });
    try {
      // ✅ Filtrar solo los lotes cuyos equipos fueron editados
      const lotesFiltrados = lotesCalibracion.filter((lote) => {
        const equipoId = lote.equipoInOt?._id ?? lote.equipoInOt;
        return equiposEditados.has(equipoId);
      });
      
      // Guardar todas las sesiones de todos los equipos en paralelo
      const promesas = [];
      lotesFiltrados.forEach((lote) => {

        const equipoId = lote.equipoInOt?._id ?? lote.equipoInOt;
        const datosEquipo = todosLosDatos[equipoId];   //obtengo los datos del equipo específico que se va a guardar ok

        // Iterar por cada magnitud (sesión)
        plantilla.magnitudesConfig.forEach((magnitudConfig, sesionIndex) => {
          const magnitudNombre = getMagnitudKey(magnitudConfig);
          const datosIBCMagnitud = datosEquipo[magnitudNombre];  //obtengo los datos del IBC para la magnitud específica que se va a guardar ok
          if (!datosIBCMagnitud) return;

          const puntosCalibracion = datosPuntosCalibracion[magnitudNombre] || [];
          // Convertir datos IBC al esquema de DB
          const medicionesIBC = convertirIBCAEsquema(
            datosIBCMagnitud,
            magnitudConfig,
            puntosCalibracion
          );

          console.log(`Guardando equipo ${equipoId} - ${magnitudNombre} (sesión ${sesionIndex}) con mediciones:`, medicionesIBC);
          
          promesas.push(
            patchMedicionIBC(lote._id, sesionIndex, { medicionesIBC })
          );
        });
      });
      
      await Promise.all(promesas);
      setAutoguardado({ estado: 'saved', tipo: 'equipos', mensaje: 'Equipos guardados correctamente' });
      if (onDataChange) onDataChange();
      setTimeout(() => setAutoguardado({ estado: 'idle', tipo: null, mensaje: '' }), 3000);
    } catch (error) {
      console.error('Error guardando equipos IBC:', error);
      // Guard NO-WIPE (spec D10): backend rechaza payload vacío que borraría mediciones.
      const codigo = error?.response?.data?.error;
      const msg = error?.response?.data?.message;
      if (codigo === 'WIPE_BLOQUEADO') {
        setAutoguardado({
          estado: 'error',
          tipo: 'equipos',
          mensaje: msg || 'No se puede guardar payload vacío sobre mediciones existentes. Registra valores o cancela.'
        });
      } else {
        setAutoguardado({ estado: 'error', tipo: 'equipos', mensaje: 'Error al guardar equipos' });
      }
    }
  }, [lotesCalibracion, plantilla, datosPuntosCalibracion, onDataChange]);

  // ═══════════════════════════════════════════════════════════════
  // GUARDAR CAMBIOS DE PUNTOS DE CALIBRACIÓN
  // ═══════════════════════════════════════════════════════════════
  const guardarCambiosPuntosCalibracion = useCallback(async (todosLosPuntos) => {
    setAutoguardado({ estado: 'saving', tipo: 'puntos', mensaje: 'Guardando puntos de calibración...' });
    try {
      // Validación defensiva
      if (!lotesCalibracion || lotesCalibracion.length === 0 || !lotesCalibracion[0] || !lotesCalibracion[0].sesiones) {
        console.error('lotesCalibracion no está disponible');
        setAutoguardado({ estado: 'error', tipo: 'puntos', mensaje: 'Error: datos no disponibles' });
        return;
      }

      // Iterar por cada sesión (magnitud) y enviar los puntos
      const promesas = lotesCalibracion[0].sesiones.map((magnitudConfig, sesionIndex) => {
        const magnitudNombre = magnitudConfig.magnitud;
        const puntosCalibracion = todosLosPuntos[magnitudNombre] || [];
        
        return patchPuntosCalibracion(loteEquipos._id, sesionIndex, { 
          puntosCalibracion 
        });
      });

      await Promise.all(promesas);
      setAutoguardado({ estado: 'saved', tipo: 'puntos', mensaje: 'Puntos guardados correctamente' });
      if (onDataChange) onDataChange();
      setTimeout(() => setAutoguardado({ estado: 'idle', tipo: null, mensaje: '' }), 3000);
    } catch (error) {
      console.error('Error guardando puntos de calibración:', error);
      setAutoguardado({ estado: 'error', tipo: 'puntos', mensaje: 'Error al guardar puntos' });
    }
  }, [loteEquipos, lotesCalibracion, onDataChange]);

  // ═══════════════════════════════════════════════════════════════
  // HANDLER DE CAMBIOS PARA PATRONES
  // ═══════════════════════════════════════════════════════════════
  const handleChangePuntoPatrones = (patronIdx, _loteId, magnitud, puntoIdx, campo, valor, medicionIdx = null) => {
    const nuevosDatos = JSON.parse(JSON.stringify(datosPatrones));

    // Validación defensiva: verificar que lotesCalibracion existe y tiene datos
    if (!lotesCalibracion || lotesCalibracion.length === 0 || !lotesCalibracion[0] || !lotesCalibracion[0].sesiones) {
      console.error('lotesCalibracion no está disponible o no tiene sesiones');
      return;
    }

    if (!nuevosDatos[patronIdx]) {
      console.error(`No existe datos para patronIdx: ${patronIdx}`);
      return;
    }
    if (!nuevosDatos[patronIdx][magnitud]) {
      console.error(`No existe datos para magnitud: ${magnitud} en patronIdx: ${patronIdx}`);
      return;
    }

    if (campo === 'puntoCal') {
      nuevosDatos[patronIdx][magnitud].puntoCalibracion[puntoIdx] = valor === '' ? '' : parseFloat(valor);
    } else if (campo === 'mediciones') {
      if (!nuevosDatos[patronIdx][magnitud].mediciones[puntoIdx]) {
        const magnitudConfig = lotesCalibracion[0].sesiones.find(m => m.magnitud === magnitud);
        nuevosDatos[patronIdx][magnitud].mediciones[puntoIdx] = Array(magnitudConfig.cantMedicionesPorPunto).fill('');
      }
      nuevosDatos[patronIdx][magnitud].mediciones[puntoIdx][medicionIdx] = valor === '' ? '' : parseFloat(valor);
    } else if (campo === 'puntoBajadaCal') {
      nuevosDatos[patronIdx][magnitud].puntoBajadaCalibracion[puntoIdx] = valor === '' ? '' : parseFloat(valor);
    } else if (campo === 'subida') {
      if (!nuevosDatos[patronIdx][magnitud].medicionesSubida[puntoIdx]) {
        const magnitudConfig = lotesCalibracion[0].sesiones.find(m => m.magnitud === magnitud);
        nuevosDatos[patronIdx][magnitud].medicionesSubida[puntoIdx] = Array(magnitudConfig.cantMedicionesPorPunto).fill('');
      }
      nuevosDatos[patronIdx][magnitud].medicionesSubida[puntoIdx][medicionIdx] = valor === '' ? '' : parseFloat(valor);
    } else if (campo === 'bajada') {
      if (!nuevosDatos[patronIdx][magnitud].medicionesBajada[puntoIdx]) {
        const magnitudConfig = lotesCalibracion[0].sesiones.find(m => m.magnitud === magnitud);
        nuevosDatos[patronIdx][magnitud].medicionesBajada[puntoIdx] = Array(magnitudConfig.cantMedicionesPorPunto).fill('');
      }
      nuevosDatos[patronIdx][magnitud].medicionesBajada[puntoIdx][medicionIdx] = valor === '' ? '' : parseFloat(valor);
    } else if (campo === 'histeresisCal') {
      if (!nuevosDatos[patronIdx][magnitud].puntoHisteresis) {
        nuevosDatos[patronIdx][magnitud].puntoHisteresis = { puntoCal: '', mediciones: [] };
      }
      nuevosDatos[patronIdx][magnitud].puntoHisteresis.puntoCal = valor === '' ? '' : parseFloat(valor);
    } else if (campo === 'histeresisMedicion') {
      if (!nuevosDatos[patronIdx][magnitud].puntoHisteresis) {
        nuevosDatos[patronIdx][magnitud].puntoHisteresis = { puntoCal: '', mediciones: [] };
      }
      nuevosDatos[patronIdx][magnitud].puntoHisteresis.mediciones[medicionIdx] = valor === '' ? '' : parseFloat(valor);
    }

    setDatosPatrones(nuevosDatos);

    patronesEditadosRef.current.add(patronIdx);

    // Autoguardado con debounce de 2 segundos
    if (timerPatronesRef.current) clearTimeout(timerPatronesRef.current);
    setAutoguardado({ estado: 'pending', tipo: 'patrones', mensaje: 'Cambios en patrones sin guardar' });
    timerPatronesRef.current = setTimeout(() => {
      const patronesEditados = new Set(patronesEditadosRef.current);
      guardarCambiosPatrones(nuevosDatos, patronesEditados);
      patronesEditadosRef.current = new Set();

    }, 30000); // Guardar cada 30 segundos o al salir del paso, lo que ocurra primero
  };

  // ═══════════════════════════════════════════════════════════════
  // HANDLER PUNTOS DE CALIBRACION PARA UNA SESION EN GENERAL (NO SOLO IBC)
  // ═══════════════════════════════════════════════════════════════
  const handlePuntosCalibracion = (idxMag, idxPunto, valor, magnitud) => {
    const nuevosDatos = { ...datosPuntosCalibracion };
    const nuevosdatosIBC = JSON.parse(JSON.stringify(datosIBC));
    
    // Validación defensiva: verificar que lotesCalibracion existe y tiene datos
    if (!lotesCalibracion || lotesCalibracion.length === 0 || !lotesCalibracion[0] || !lotesCalibracion[0].sesiones) {
      console.error('lotesCalibracion no está disponible o no tiene sesiones');
      return;
    }
    
    // Inicializar si no existe
    if (!nuevosDatos[magnitud]) {
      const magnitudConfig = lotesCalibracion[0].sesiones.find(m => m.magnitud === magnitud);
      nuevosDatos[magnitud] = Array(magnitudConfig?.cantPuntos || 0).fill('');
    }
    
    // Actualizar solo el punto específico
    nuevosDatos[magnitud][idxPunto] = valor === '' ? '' : parseFloat(valor);

    // se hace un forEach de los equipos para actualizar el punto de calibracion en cada uno de ellos (datosIBC), pero SOLO si el cambio es en un punto de calibracion (no en mediciones) y si la magnitud coincide
    Object.keys(nuevosdatosIBC).forEach(equipoId => {
      const equipo = nuevosdatosIBC[equipoId];
      if (!equipo[magnitud]) {  //
        equipo[magnitud] = {
            puntoCalibracion: [...nuevosDatos[magnitud]],
            mediciones: Array(lotesCalibracion[0].sesiones.find(m => m.magnitud === magnitud)?.cantPuntos || 0).fill(null).map(() => Array(lotesCalibracion[0].sesiones.find(m => m.magnitud === magnitud)?.cantMedicionesPorPunto || 0).fill(''))
        };
      } else {
        equipo[magnitud].puntoCalibracion = [...nuevosDatos[magnitud]];
      }
    });

    setDatosIBC(nuevosdatosIBC);
    setDatosPuntosCalibracion(nuevosDatos);

    // Autoguardado con debounce de 90 segundos
    if (timerPuntosCalibracionRef.current) clearTimeout(timerPuntosCalibracionRef.current);
    setAutoguardado({ estado: 'pending', tipo: 'puntos', mensaje: 'Cambios en puntos de calibración sin guardar' });
    timerPuntosCalibracionRef.current = setTimeout(() => {
      guardarCambiosPuntosCalibracion(nuevosDatos);
    }, 30000); // Guardar cada 30 segundos o al salir del paso, lo que ocurra primero
  }

    // ═══════════════════════════════════════════════════════════════
  // HANDLER PUNTOS DE HISTERESIS PARA UNA SESION EN GENERAL (NO SOLO IBC)
  // ═══════════════════════════════════════════════════════════════
    const handlePuntosHisteresis = (idxMag, valor, magnitud, tipo) => {
        const nuevosDatos = { ...datoHisteresis };

        const nuevosdatosIBC = JSON.parse(JSON.stringify(datosIBC)); //Datos del IBC para actualizar el punto de histéresis en cada equipo

        // Validación defensiva: verificar que lotesCalibracion existe y tiene datos
        if (!lotesCalibracion || lotesCalibracion.length === 0 || !lotesCalibracion[0] || !lotesCalibracion[0].sesiones) {
          console.error('lotesCalibracion no está disponible o no tiene sesiones');
          return;
        }

        // Actualizar el valor de histéresis (es solo un valor, no un array ni objeto)
        nuevosDatos[magnitud] = valor === '' ? '' : parseFloat(valor);

        // Actualizar el punto de histéresis en cada equipo del IBC
        Object.keys(nuevosdatosIBC).forEach(equipoId => {
            const equipo = nuevosdatosIBC[equipoId];
            const magnitudConfig = lotesCalibracion[0].sesiones.find(m => m.magnitud === magnitud);
            
            if (!equipo[magnitud]) { 
                // Crear estructura completa si no existe
                equipo[magnitud] = {
                    puntoCalibracion: Array(magnitudConfig?.cantPuntos || 0).fill(''),
                    mediciones: Array(magnitudConfig?.cantPuntos || 0).fill(null).map(() => Array(magnitudConfig?.cantMedicionesPorPunto || 0).fill('')),
                    puntoHisteresis: {
                        puntoCal: '',
                        mediciones: Array(magnitudConfig?.cantMedicionesPorPunto || 0).fill('')
                    }
                };
            } else if (!equipo[magnitud].puntoHisteresis) { 
                // Agregar solo estructura de histéresis si falta
                equipo[magnitud].puntoHisteresis = {
                    puntoCal: '',
                    mediciones: Array(magnitudConfig?.cantMedicionesPorPunto || 0).fill('')
                };
            }
            
            // Actualizar el valor de puntoCal en la estructura correcta
            equipo[magnitud].puntoHisteresis.puntoCal = valor === '' ? '' : parseFloat(valor);
        });

        setDatosIBC(nuevosdatosIBC);
        setDatoHisteresis(nuevosDatos);

        // Bug fix: el puntoCal de histéresis se guarda como parte del `puntoHisteresis`
        // dentro de cada equipoInOt, así que hay que marcar TODOS los equipos como
        // editados y disparar el autoguardado colectivo de IBC. Sin esto el valor
        // solo vivía en el estado React y se perdía al refrescar.
        Object.keys(nuevosdatosIBC).forEach((equipoId) => {
            equiposEditadosRef.current.add(equipoId);
        });

        Object.values(timersEquiposRef.current).forEach((timer) => clearTimeout(timer));
        timersEquiposRef.current = {};
        setAutoguardado({ estado: 'pending', tipo: 'equipos', mensaje: 'Cambios en histéresis sin guardar' });
        // 2s en lugar de 30s — el usuario reporta que "al darle guardar no se
        // guarda", esperaba persistencia rápida cuando termina de escribir.
        timersEquiposRef.current.global = setTimeout(() => {
            const editados = new Set(equiposEditadosRef.current);
            equiposEditadosRef.current.clear();
            guardarCambiosEquipos(nuevosdatosIBC, editados);
        }, 2000);
    }

  // ═══════════════════════════════════════════════════════════════
  // HANDLER DE CAMBIOS PARA IBC
  // ═══════════════════════════════════════════════════════════════
  const handleChangePunto = (equipoId, loteId, magnitud, puntoIdx, campo, valor, medicionIdx = null, idxMag = null) => {
  
    const nuevosDatos = JSON.parse(JSON.stringify(datosIBC));
    // Validación defensiva: verificar que lotesCalibracion existe y tiene datos
    if (!lotesCalibracion || lotesCalibracion.length === 0 || !lotesCalibracion[0] || !lotesCalibracion[0].sesiones) {
      console.error('lotesCalibracion no está disponible o no tiene sesiones');
      return;
    }
    
    // Validación defensiva: asegurar que la estructura existe
    if (!nuevosDatos[equipoId]) {
      console.error(`No existe datos para equipoId: ${equipoId}`);
      return;
    }
    if (!nuevosDatos[equipoId][magnitud]) {  // Esto puede pasar si la plantilla cambió y ahora tiene una magnitud nueva que no existía antes
      console.error(`No existe datos para magnitud: ${magnitud} en equipoId: ${equipoId}`);
      return;
    }

    // Obtener config de magnitud para inicializaciones
    const magnitudConfig = lotesCalibracion[0].sesiones.find(m => m.magnitud === magnitud);
    
    if (!magnitudConfig) {
      console.error(`No se encontró configuración para magnitud: ${magnitud}`);
      return;
    }

    // Casos normales (puntos regulares)
    if (campo === 'puntoCal') {
      nuevosDatos[equipoId][magnitud].puntoCalibracion[puntoIdx] = valor === '' ? '' : parseFloat(valor);
    } else if (campo === 'mediciones') {
      // Validar que el array existe antes de asignar
      if (!nuevosDatos[equipoId][magnitud].mediciones[puntoIdx]) {
        nuevosDatos[equipoId][magnitud].mediciones[puntoIdx] = Array(magnitudConfig.cantMedicionesPorPunto).fill('');
      }
      nuevosDatos[equipoId][magnitud].mediciones[puntoIdx][medicionIdx] = valor === '' ? '' : parseFloat(valor);
    }
    // Casos de histéresis en todos los puntos (subida/bajada)
    else if (campo === 'puntoBajadaCal') {

      nuevosDatos[equipoId][magnitud].puntoBajadaCalibracion[puntoIdx] = valor === '' ? '' : parseFloat(valor);
    } else if (campo === 'subida') {

      console.log('actualizando punto S/B: ', equipoId, magnitud, puntoIdx, medicionIdx, valor);
      // Validar que el array existe antes de asignar
      if (!nuevosDatos[equipoId][magnitud].medicionesSubida[puntoIdx]) {
        nuevosDatos[equipoId][magnitud].medicionesSubida[puntoIdx] = Array(magnitudConfig.cantMedicionesPorPunto).fill('');
      }
      nuevosDatos[equipoId][magnitud].medicionesSubida[puntoIdx][medicionIdx] = valor === '' ? '' : parseFloat(valor);
    } else if (campo === 'bajada') {
      // Validar que el array existe antes de asignar
      if (!nuevosDatos[equipoId][magnitud].medicionesBajada[puntoIdx]) {
        nuevosDatos[equipoId][magnitud].medicionesBajada[puntoIdx] = Array(magnitudConfig.cantMedicionesPorPunto).fill('');
      }
      nuevosDatos[equipoId][magnitud].medicionesBajada[puntoIdx][medicionIdx] = valor === '' ? '' : parseFloat(valor);
    }
    // Casos de punto adicional de histéresis
    else if (campo === 'histeresisCal') {
      if (!nuevosDatos[equipoId][magnitud].puntoHisteresis) {
        nuevosDatos[equipoId][magnitud].puntoHisteresis = { puntoCal: '', mediciones: [] };
      }
      nuevosDatos[equipoId][magnitud].puntoHisteresis.puntoCal = valor === '' ? '' : parseFloat(valor);
    } else if (campo === 'histeresisMedicion') {
      if (!nuevosDatos[equipoId][magnitud].puntoHisteresis) {
        nuevosDatos[equipoId][magnitud].puntoHisteresis = { puntoCal: '', mediciones: [] };
      }
      if (!nuevosDatos[equipoId][magnitud].puntoHisteresis.mediciones) {
        nuevosDatos[equipoId][magnitud].puntoHisteresis.mediciones = Array(magnitudConfig.cantMedicionesPorPunto).fill('');
      }
      nuevosDatos[equipoId][magnitud].puntoHisteresis.mediciones[medicionIdx] = valor ==='' ? '' : parseFloat(valor);
    }

    setDatosIBC(nuevosDatos);


    // ✅ Registrar el equipo editado
    equiposEditadosRef.current.add(equipoId);

    // Autoguardado colectivo con debounce de 2 segundos
    // Cancelar todos los timers de equipos pendientes
    Object.values(timersEquiposRef.current).forEach(timer => clearTimeout(timer));
    timersEquiposRef.current = {};

    setAutoguardado({ estado: 'pending', tipo: 'equipos', mensaje: 'Cambios en equipos sin guardar' });

    // Crear un único timer para guardar todos los datos
    timersEquiposRef.current.global = setTimeout(() => {
      // Pasar snapshot del Set y limpiarlo
      const editados = new Set(equiposEditadosRef.current);
      equiposEditadosRef.current.clear();
      guardarCambiosEquipos(nuevosDatos, editados);
    }, 30000); // Guardar cada 30 segundos o al salir del paso, lo que ocurra primero
  };

  // ═══════════════════════════════════════════════════════════════
  // CLEANUP DE TIMERS
  // ═══════════════════════════════════════════════════════════════
  useEffect(() => {
    return () => {
      // Limpiar timer de patrones
      if (timerPatronesRef.current) clearTimeout(timerPatronesRef.current);
      // Limpiar timers de equipos
      Object.values(timersEquiposRef.current).forEach(timer => clearTimeout(timer));
      // Limpiar timer de puntos de calibración
      if (timerPuntosCalibracionRef.current) clearTimeout(timerPuntosCalibracionRef.current);
    };
  }, []);

  // ═══════════════════════════════════════════════════════════════
  // FUNCIÓN PARA GUARDAR AHORA (EXPUESTA AL PADRE)
  // ═══════════════════════════════════════════════════════════════
  const guardarAhora = useCallback(async () => {
    const promesas = [];

    // Cancelar todos los timers
    if (timerPatronesRef.current) {
      clearTimeout(timerPatronesRef.current);
      timerPatronesRef.current = null;
    }
    Object.keys(timersEquiposRef.current).forEach(key => {
      clearTimeout(timersEquiposRef.current[key]);
      delete timersEquiposRef.current[key];
    });
    if (timerPuntosCalibracionRef.current) {
      clearTimeout(timerPuntosCalibracionRef.current);
      timerPuntosCalibracionRef.current = null;
    }

    // Bug fix: antes era `if / else if / else if` → cuando el usuario tenía
    // cambios pendientes en varios tipos (p. ej. llenó patrones Y equipos),
    // solo se disparaba UNO (el que match el `autoguardado.tipo` — el ÚLTIMO
    // tipo tocado). El resto se perdía. Ahora los tres se disparan
    // independientemente en paralelo si tienen refs con cambios acumulados.
    if (patronesEditadosRef.current.size > 0) {
      const patronesEditados = new Set(patronesEditadosRef.current);
      promesas.push(guardarCambiosPatrones(datosPatrones, patronesEditados));
      patronesEditadosRef.current = new Set();
    }
    if (equiposEditadosRef.current.size > 0) {
      const equiposEditados = new Set(equiposEditadosRef.current);
      promesas.push(guardarCambiosEquipos(datosIBC, equiposEditados));
      equiposEditadosRef.current = new Set();
    }
    if (autoguardado.tipo === 'puntos') {
      promesas.push(guardarCambiosPuntosCalibracion(datosPuntosCalibracion));
    }

    // Esperar a que todas las promesas se completen
    if (promesas.length > 0) {
      await Promise.all(promesas);
    }
  }, [datosPatrones, datosIBC, datosPuntosCalibracion, autoguardado, guardarCambiosPatrones, guardarCambiosEquipos, guardarCambiosPuntosCalibracion]);

  // Exponer el método guardarAhora al componente padre
  useImperativeHandle(ref, () => ({
    guardarAhora,
    tieneCambiosPendientes: () => autoguardado.estado === 'pending'
  }), [guardarAhora, autoguardado]);

  // ═══════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════

  // Validación temprana - no renderizar si los datos no están listos
  if (!lotesCalibracion || lotesCalibracion.length === 0 || !lotesCalibracion[0] || !lotesCalibracion[0].sesiones) {
    return (
      <div className="text-center p-5">
        <Alert variant="warning">
          Cargando datos de calibración...
        </Alert>
      </div>
    );
  }

  const calcularCompletoEquipo = (equipoId) => {
    const datos = datosIBC[equipoId];
    if (!datos) return false;

    for (let magnitud of Object.keys(datos)) {
      const puntos = datos[magnitud].puntoCalibracion || [];
      const mediciones = datos[magnitud].mediciones || [];

      for (let i = 0; i < puntos.length; i++) {
        if (puntos[i] === '' || !mediciones[i] || mediciones[i].some(m => m === '')) {
          return false;
        }
      }
    }

    return true;
  };

  // Crear estructura virtual de "lotes" para patrones.
  // En la columna "Equipo" mostramos el `inventario` del patrón (identificador
  // interno del inventario del laboratorio) — más práctico para el metrólogo
  // que el genérico "Patrón 1" o el código.
  // Usar cantPatronesPrincipales + patronesPrincipalesDelLote (memos de arriba)
  // para respetar la personalización del Step 1 y excluir medios.
  const cantPatrones = cantPatronesPrincipales;
  const patronesVirtuales = Array.from({ length: cantPatrones }).map((_, idx) => {
    const patronReal = patronesPrincipalesDelLote[idx] || null;

    const label = patronReal?.inventario
      || patronReal?.codigo
      || `Patrón ${idx + 1}`;

    return {
      _id: idx,
      equipoInOt: {
        _id: idx,
        consecutivo: label,
        equipo: {
          item: {
            nombre: patronReal?.descripcion || patronReal?.nombre || patronReal?.item || ''
          },
          serie: patronReal?.serie || ''
        }
      }
    };
  });

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h5 className="mb-0">Mediciones de Calibración</h5>
        <AutoguardadoIndicator 
          estado={autoguardado.estado} 
          mensaje={autoguardado.mensaje}
          onReintentar={() => {
            if (autoguardado.tipo === 'patrones') {
              guardarCambiosPatrones(datosPatrones, new Set(patronesEditadosRef.current));
            } else if (autoguardado.tipo === 'equipos') {
              guardarCambiosEquipos(datosIBC, new Set(equiposEditadosRef.current));
            } else if (autoguardado.tipo === 'puntos') {
              guardarCambiosPuntosCalibracion(datosPuntosCalibracion);
            }
          }}
          onGuardarAhora={guardarAhora}
        />
      </div>

      <Alert variant="info">
        <strong>Instrucción:</strong> Registre las mediciones de patrones e IBC para cada magnitud.
        Los cambios se guardan automáticamente.
      </Alert>

      <Card className="shadow-sm mb-4 w-100" style={{borderRadius: '12px'}}>
        <Card.Header className="bg-dark text-white d-flex justify-content-between align-items-center w-100" style={{borderRadius: '12px 12px 0 0'}}>
          <span>Mediciones por Magnitud</span>
        </Card.Header>
        <Card.Body className="p-2 w-100">
          <Accordion defaultActiveKey="0">
            {(lotesCalibracion[0].sesiones || []).map((magnitud, idxMag) => {
              return (
                <Accordion.Item key={idxMag} eventKey={idxMag.toString()}>
                  <Accordion.Header>{magnitud.magnitud}</Accordion.Header>
                  <Accordion.Body>
                    {/* ─────────────────────────────────────────────── */}
                    {/* FORMULARIO DE PUNTOS DE CALIBRACIÓN */}
                    {/* ─────────────────────────────────────────────── */}
                    <Card className="mb-4 w-100" style={{borderRadius: '12px'}}>
                      <Card.Body className='w-100'>
                        <h6 className="mb-3 text-secondary">Puntos de Calibración</h6>
                        <PuntosCalibracionForm
                          magnitud={magnitud}
                          datosPuntosCalibracion={datosPuntosCalibracion[magnitud.magnitud] || []}
                          datoHisteresis={datoHisteresis[magnitud.magnitud] || ''}
                          onChangePunto={(idxPunto, valor) => handlePuntosCalibracion(idxMag, idxPunto, valor, magnitud.magnitud)}
                          onChangeHisteresis={(valor) => handlePuntosHisteresis(idxMag, valor, magnitud.magnitud)}
                        />
                      </Card.Body>
                    </Card>

                    {/* ─────────────────────────────────────────────── */}
                    {/* TABLA DE PATRONES */}
                    {/* ─────────────────────────────────────────────── */}
                    <div className="mb-4">
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h6 className="mb-0 text-primary">
                          📊 Mediciones de Patrones
                          <small className="ms-2 text-muted">
                            ({cantPatrones} {cantPatrones === 1 ? 'patrón' : 'patrones'})
                          </small>
                        </h6>
                      </div>
                      <PuntosMedicionTable
                        magnitud={magnitud}
                        lotesCalibracion={patronesVirtuales}
                        datosIBC={datosPatrones}
                        datosPuntosCalibracion={datosPuntosCalibracion[magnitud.magnitud]}
                        datoHisteresis={datoHisteresis [magnitud.magnitud]}
                        tabTipo="patrones"
                        onChangePunto={(patronIdx, loteId, campo, valor, medicionIdx, puntoIdx) =>
                          handleChangePuntoPatrones(patronIdx, loteId, magnitud.magnitud, puntoIdx, campo, valor, medicionIdx, idxMag)
                        }
                      />
                    </div>

                    {/* ─────────────────────────────────────────────── */}
                    {/* SEPARADOR */}
                    {/* ─────────────────────────────────────────────── */}
                    <hr className="my-4" style={{borderTop: '2px solid #dee2e6'}} />

                    {/* ─────────────────────────────────────────────── */}
                    {/* TABLA DE IBC */}
                    {/* ─────────────────────────────────────────────── */}
                    <div>
                      <h6 className="mb-3 text-dark">
                        📊 Mediciones IBC (Instrumentos Bajo Calibración)
                        <small className="ms-2 text-muted">
                          ({lotesCalibracion?.length || 0} {lotesCalibracion?.length === 1 ? 'equipo' : 'equipos'})
                        </small>
                      </h6>
                      <PuntosMedicionTable
                        magnitud={magnitud}
                        lotesCalibracion={lotesCalibracion}
                        datosPuntosCalibracion={datosPuntosCalibracion[magnitud.magnitud]}
                        datosIBC={datosIBC}
                        tabTipo="ibc"
                        onChangePunto={(equipoId, loteId, campo, valor, medicionIdx, puntoIdx) =>
                          handleChangePunto(equipoId, loteId, magnitud.magnitud, puntoIdx, campo, valor, medicionIdx, idxMag)
                        }
                      />
                    </div>

                  </Accordion.Body>
                </Accordion.Item>
              );
            })}
          </Accordion>
        </Card.Body>
      </Card>
    </div>
  );
});

export default StepIBC;
